<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError, useApiClient } from '@yuksalish/api-client'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { createThreadCode } from '../productService'
import type { ThreadCodeReference } from '../types'

const props = defineProps<{
  open: boolean
  title: string
  items: ThreadCodeReference[]
}>()

const emit = defineEmits<{
  close: []
  select: [item: ThreadCodeReference]
  created: [item: ThreadCodeReference]
}>()

const client = useApiClient()
const auth = useAuthStore()
const search = ref('')
const page = ref(1)
const createOpen = ref(false)
const saving = ref(false)
const errorMessage = ref('')
const createForm = reactive({
  code: '',
  name: '',
  yarn_type: '',
  yarn_composition: '',
})
const pageSize = 8
const canCreate = computed(() => auth.user?.permissions.includes('products.update') ?? false)
const filteredItems = computed(() => {
  const query = search.value.trim().toLocaleLowerCase()
  if (!query) return props.items
  return props.items.filter((item) =>
    [item.code, item.name, item.yarn_type, item.yarn_composition]
      .some((value) => String(value ?? '').toLocaleLowerCase().includes(query)),
  )
})
const totalPages = computed(() => Math.max(1, Math.ceil(filteredItems.value.length / pageSize)))
const visibleItems = computed(() => {
  const start = (page.value - 1) * pageSize
  return filteredItems.value.slice(start, start + pageSize)
})
const visibleOptions = computed<SmartPickerOption[]>(() =>
  visibleItems.value.map((item) => ({
    id: item.id,
    title: item.code,
    subtitle: item.name || 'No name',
    meta: [item.yarn_type, item.yarn_composition].filter(Boolean).join(' · ') || `#${item.id}`,
  })),
)

function resetCreateForm(): void {
  createForm.code = ''
  createForm.name = ''
  createForm.yarn_type = ''
  createForm.yarn_composition = ''
  errorMessage.value = ''
}

watch(() => props.open, (open) => {
  if (open) {
    search.value = ''
    page.value = 1
  }
}, { immediate: true })

watch(search, () => {
  if (!props.open) return
  page.value = 1
})

watch(createOpen, (open) => {
  if (open) resetCreateForm()
})

function selectOption(option: SmartPickerOption): void {
  const item = visibleItems.value.find((entry) => entry.id === Number(option.id))
  if (item) emit('select', item)
}

async function submitCreate(): Promise<void> {
  saving.value = true
  errorMessage.value = ''
  try {
    const item = await createThreadCode(client, {
      code: createForm.code.trim(),
      name: createForm.name.trim(),
      yarn_type: createForm.yarn_type.trim() || undefined,
      yarn_composition: createForm.yarn_composition.trim() || undefined,
      is_active: 1,
    })
    createOpen.value = false
    emit('created', item)
    emit('select', item)
  } catch (error: unknown) {
    errorMessage.value = formatApiError(error)
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title"
    eyebrow="Thread code repository"
    search-label="Search thread codes"
    search-placeholder="Code, name, yarn type"
    :search-value="search"
    :loading="false"
    error-message=""
    empty-message="No matching thread codes found."
    :items="visibleOptions"
    :page="page"
    :total-pages="totalPages"
    :page-label="`${filteredItems.length} items · Page ${page} of ${totalPages}`"
    close-aria-label="Close thread code picker"
    retry-label="Retry"
    previous-label="Previous"
    next-label="Next"
    cancel-label="Cancel"
    @close="emit('close')"
    @previous="page--"
    @next="page++"
    @select="selectOption"
    @update:search-value="search = $event"
  >
    <template #helper>
      <div class="picker-helper">
        <div>
          <strong>Need a new thread code?</strong>
          <p>Create it here, then it will be selected automatically.</p>
        </div>
        <button type="button" :disabled="!canCreate" @click="createOpen = true">Add new</button>
      </div>
    </template>
  </SmartPickerModal>
  <Teleport to="body">
    <div v-if="createOpen" class="modal-backdrop" @click.self="createOpen = false">
      <section class="create-modal" role="dialog" aria-modal="true" aria-label="Create thread code">
        <header>
          <div>
            <p class="eyebrow">Repository tool</p>
            <h3>Create thread code</h3>
          </div>
          <button class="close-button" type="button" aria-label="Close create form" @click="createOpen = false">×</button>
        </header>
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        <label><span>Code</span><input v-model="createForm.code" type="text" placeholder="30-001"></label>
        <label><span>Name</span><input v-model="createForm.name" type="text" placeholder="30-001 Black Main Thread"></label>
        <label><span>Yarn type</span><input v-model="createForm.yarn_type" type="text" placeholder="cotton"></label>
        <label><span>Yarn composition</span><input v-model="createForm.yarn_composition" type="text" placeholder="30/1"></label>
        <footer>
          <button class="secondary-button" type="button" @click="createOpen = false">Cancel</button>
          <button type="button" :disabled="saving || !createForm.code.trim() || !createForm.name.trim()" @click="submitCreate">
            {{ saving ? 'Saving...' : 'Create' }}
          </button>
        </footer>
      </section>
    </div>
  </Teleport>
</template>

<style scoped>
.picker-helper { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; border: 1px solid #d7dfdc; background: #f7fbff; }
.picker-helper strong { color: #173454; font-size: 13px; }
.picker-helper p { margin: 0; color: #66726e; font-size: 12px; line-height: 1.4; }
.picker-helper button, .secondary-button, footer button:last-child { padding: 8px 12px; border: 1px solid #b9c5c1; font: inherit; font-weight: 700; cursor: pointer; }
.picker-helper button, .secondary-button { color: #285b9a; background: #fff; }
.picker-helper button:disabled { color: #9aa7a3; background: #f3f6f5; cursor: default; }
.modal-backdrop { position: fixed; z-index: 1100; inset: 0; display: grid; padding: 24px; background: rgb(5 31 67 / 52%); place-items: center; }
.create-modal { display: grid; width: min(460px, 100%); gap: 12px; padding: 20px; border: 1px solid #cbd7e5; background: #fff; box-shadow: 0 24px 60px rgb(5 31 67 / 25%); }
header, footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.eyebrow { margin: 0 0 4px; color: #66726e; font-size: 11px; font-weight: 800; text-transform: uppercase; }
h3, p { margin: 0; }
label { display: grid; gap: 6px; color: #53625d; font-size: 13px; font-weight: 700; }
input { padding: 11px 12px; border: 1px solid #aebdcc; font: inherit; }
.close-button { padding: 2px 10px; color: #66726e; background: transparent; font-size: 28px; }
footer button:last-child { color: #fff; background: #285b9a; }
.error-message { color: #852d22; }
</style>
